import React, { useCallback, useEffect, useState } from 'react';
import { formatAmount } from '../lib/money';
import { useNavigate } from 'react-router-dom';
import { Badge, Banner, Card, EmptyState, KpiTile, PageContainer, PageHeader, Spinner } from '../ui/components';
import { Icon } from '../ui/icons';
import { useAuth } from '../auth/AuthContext';
import { useProperty } from '../app/PropertyContext';
import { useT } from '../i18n';
import { purchaseOrderPath } from '../lib/slug';
import { dashboardApi } from '../features/dashboard/dashboardApi';
import './pages.css';
import './dashboard.css';

// Headline money: skip cents on whole amounts — IDR sums are huge and never fractional,
// and the trailing ".00" is what pushes long values out of the KPI cards.
const money = (cur, n) => {
  const v = Number(n) || 0;
  return `${cur || 'USD'} ${formatAmount(v, Math.abs(v % 1) < 0.005 ? 0 : 2)}`;
};

const DOC_LABEL = { Requisition: 'Requisition', PurchaseOrder: 'PO', Invoice: 'Invoice' };
const DOC_TONE = { Requisition: 'neutral', PurchaseOrder: 'brand', Invoice: 'info' };

function timeAgo(iso) {
  if (!iso) return '';
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 3600) return `${Math.max(1, Math.floor(secs / 60))}m`;
  const hrs = Math.floor(secs / 3600);
  if (hrs < 24) return `${hrs}h`;
  return `${Math.floor(hrs / 24)}d`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { activeProperty, properties } = useProperty();
  const t = useT();
  const navigate = useNavigate();
  const greetingName = user?.firstName || t('there');

  const [summary, setSummary] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, i] = await Promise.all([dashboardApi.summary(), dashboardApi.approvalsInbox()]);
      setSummary(s);
      setInbox(i || []);
    } catch (e) {
      setError(e?.message || t('Failed to load the dashboard.'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load, activeProperty?.id]);

  // The server sends the property's currency of record; only fall back to the active property's
  // base currency (never a hardcoded USD) if the summary hasn't loaded yet.
  const cur =
    summary?.currency ||
    activeProperty?.baseCurrency ||
    (properties?.length === 1 ? properties[0]?.baseCurrency : null) ||
    'USD';

  const propertyName = (id) => (id ? properties?.find((p) => p.id === id)?.name : null);

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Welcome, {name}', { name: greetingName })}
        description={t('Your procurement and inventory at a glance.')}
        meta={<Badge tone="brand">{activeProperty?.name || t('All properties')}</Badge>}
      />

      {error && <Banner tone="danger">{error}</Banner>}

      {loading && !summary ? (
        <div className="dash-loading">
          <Spinner />
        </div>
      ) : (
        summary && (
          <>
            <div className="pp-kpi-grid">
              <KpiTile
                className="dash-kpi"
                label={t('My approvals')}
                value={summary.myApprovalsCount}
                meta={summary.myApprovalsCount > 0 ? t('Waiting on you') : t('Nothing pending')}
              />
              <KpiTile
                className="dash-kpi dash-kpi--click"
                label={t('Open purchase orders')}
                value={summary.openPoCount}
                meta={money(cur, summary.openPoValue)}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/purchase-orders')}
              />
              <KpiTile
                className="dash-kpi dash-kpi--click"
                label={t('Awaiting receipt')}
                value={summary.awaitingReceiptCount}
                meta={t('POs not fully received')}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/receiving')}
              />
              <KpiTile
                className="dash-kpi dash-kpi--click"
                label={t('Overdue deliveries')}
                value={summary.overdueDeliveryCount ?? 0}
                meta={summary.overdueDeliveryCount > 0 ? t('Past their expected date') : t('Nothing overdue')}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/purchase-orders')}
              />
              <KpiTile
                className="dash-kpi dash-kpi--click"
                label={t('Invoices to review')}
                value={summary.invoicesToReviewCount}
                meta={t('Draft + in approval')}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/invoices')}
              />
              <KpiTile
                className="dash-kpi dash-kpi--click dash-kpi--money"
                label={t('AP outstanding')}
                value={money(cur, summary.apOutstandingTotal)}
                meta={
                  summary.apOverdueTotal > 0
                    ? t('{amount} overdue ({count})', {
                        amount: money(cur, summary.apOverdueTotal),
                        count: summary.apOverdueCount,
                      })
                    : t('Nothing overdue')
                }
                role="button"
                tabIndex={0}
                onClick={() => navigate('/invoices')}
              />
              <KpiTile
                className="dash-kpi dash-kpi--click dash-kpi--money"
                label={t('Inventory value')}
                value={money(cur, summary.inventoryValue)}
                meta={t('On-hand at average cost')}
                role="button"
                tabIndex={0}
                onClick={() => navigate('/inventory')}
              />
            </div>

            <div className="pp-card-grid">
              <Card>
                <Card.Header>
                  <Card.Title>{t('My approvals')}</Card.Title>
                  <Card.Subtitle>{t('Documents waiting on your decision')}</Card.Subtitle>
                </Card.Header>
                <Card.Body>
                  {inbox.length === 0 ? (
                    <EmptyState
                      icon={<Icon.ClipboardList size={28} />}
                      title={t('No approvals waiting')}
                      description={t('When a requisition, purchase order or invoice needs your sign-off, it shows up here.')}
                    />
                  ) : (
                    <ul className="dash-list">
                      {inbox.map((it) => (
                        <li key={`${it.docType}-${it.id}`}>
                          <button type="button" className="dash-row" onClick={() => navigate(it.link)}>
                            <span className="dash-row__main">
                              <span className="dash-row__top">
                                <Badge tone={DOC_TONE[it.docType] || 'neutral'}>
                                  {t(DOC_LABEL[it.docType] || it.docType)}
                                </Badge>
                                <span className="dash-row__num">{it.number}</span>
                                {it.onBehalf && <Badge tone="warning">{t('On behalf')}</Badge>}
                              </span>
                              <span className="dash-row__sub">
                                {it.title || it.requester || '—'}
                                {it.stepRole ? ` · ${it.stepRole}` : ''}
                                {!activeProperty && propertyName(it.propertyId) ? ` · ${propertyName(it.propertyId)}` : ''}
                              </span>
                            </span>
                            <span className="dash-row__side">
                              <span className="dash-row__amount">{money(it.currency, it.amount)}</span>
                              {it.waitingSince && (
                                <span className="dash-row__time">
                                  <Icon.Clock size={12} /> {timeAgo(it.waitingSince)}
                                </span>
                              )}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card.Body>
              </Card>

              {(summary.overdueDeliveries?.length ?? 0) > 0 && (
                <Card>
                  <Card.Header>
                    <Card.Title>{t('Overdue deliveries')}</Card.Title>
                    <Card.Subtitle>{t('Purchase orders past their expected date without delivery')}</Card.Subtitle>
                  </Card.Header>
                  <Card.Body>
                    <ul className="dash-list">
                      {summary.overdueDeliveries.map((po) => (
                        <li key={po.id}>
                          <button type="button" className="dash-row" onClick={() => navigate(purchaseOrderPath(po))}>
                            <span className="dash-row__main">
                              <span className="dash-row__top">
                                <span className="dash-row__num">{po.number}</span>
                                <Badge tone="warning">{t('{n} day(s) late', { n: po.daysOverdue })}</Badge>
                              </span>
                              <span className="dash-row__sub">
                                {po.supplierName || '—'}
                                {` · ${t('expected {date}', { date: new Date(po.expectedDate).toLocaleDateString() })}`}
                              </span>
                            </span>
                            <span className="dash-row__side">
                              <span className="dash-row__amount dash-row__amount--warn">{money(po.currency, po.total)}</span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </Card.Body>
                </Card>
              )}

              <Card>
                <Card.Header>
                  <Card.Title>{t('Low-stock alerts')}</Card.Title>
                  <Card.Subtitle>{t('Items at or below their reorder point')}</Card.Subtitle>
                </Card.Header>
                <Card.Body>
                  {(!summary.lowStockItems || summary.lowStockItems.length === 0) ? (
                    <EmptyState
                      icon={<Icon.Boxes size={28} />}
                      title={t('Nothing to reorder')}
                      description={t('Set par levels on your catalog items to get reorder alerts here.')}
                    />
                  ) : (
                    <ul className="dash-list">
                      {summary.lowStockItems.map((it) => (
                        <li key={`${it.itemId}-${it.locationId}`}>
                          <button
                            type="button"
                            className="dash-row"
                            onClick={() => navigate(`/catalog/items/${it.itemId}`)}
                          >
                            <span className="dash-row__main">
                              <span className="dash-row__num">{it.name || it.sku || it.itemId}</span>
                              {it.sku && <span className="dash-row__sub">{it.sku}</span>}
                            </span>
                            <span className="dash-row__side">
                              <span className="dash-row__amount dash-row__amount--warn">
                                {Number(it.onHand)} / {Number(it.parLevel)}
                              </span>
                              <span className="dash-row__time">{t('on hand / par')}</span>
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card.Body>
              </Card>
            </div>
          </>
        )
      )}
    </PageContainer>
  );
}
